import React from 'react';
import { Modal, Stack } from 'react-bootstrap';
import { LomavisDownloadDomElementButton } from '@/lomavisSharedUi/atoms/buttons/LomavisDownloadDomElementButton';
import { LomavisH3 } from '@/lomavisSharedUi/atoms/typography/LomavisH3';

import LomavisMoreInfoIcon from '../../icons/LomavisMoreInfoIcon/LomavisMoreInfoIcon';
import {
  LomavisDownloadExcelElement,
  LomavisDownloadExcelElementProps
} from '../../buttons/LomavisDownloadExcelElement';
import { useTranslate } from '@tolgee/react';

interface LomavisCard1Props extends Partial<LomavisDownloadExcelElementProps> {
  /** Title of the card, displayed at the top */
  title: string;

  /** Optional subtitle to be shown behind the title */
  subtitle?: React.ReactNode;

  /** Content to render inside the card */
  children: React.ReactNode;
  /** Optional custom class names to extend or override default styles */
  className?: string;
  customDownload?: (cardRef) => Promise<void>;
  /** Whether to show the download button (default: false) */
  showDownloadButton?: boolean;

  /** Whether to show the download button (default: false) */
  showSheetDownloadButton?: boolean;
  /** Whether to show the link button (default: false) */
  showLinkButton?: boolean;
  /** URL for the link button */
  linkButtonUrl?: string;
  /** Text to display on the link button */
  linkButtonText?: string;

  /** Optional tooltip text for the title */
  tooltipText?: string;
  /** show more info icon */
  showMoreInfo?: boolean;

  /** Enable preview icon to open content in a fullscreen modal */
  enablePreview?: boolean;
}

/**
 * Atoms: LomavisCard1
 *
 * A white card container with padding, rounded corners, and a shadow.
 * Useful for wrapping content sections in a consistent style.
 */
export const LomavisCard1: React.FC<LomavisCard1Props> = ({
  title = '',
  subtitle,
  children,
  className = '',
  showDownloadButton = true,
  showSheetDownloadButton = false,
  showLinkButton = false,
  showMoreInfo = true,
  enablePreview = false,
  linkButtonUrl,
  linkButtonText,
  customDownload,

  excelData,
  elementRef,
  dataExtractor,
  staticData
}) => {
  const { t } = useTranslate();
  const [showPreview, setShowPreview] = React.useState(false);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const previewLabel = t('preview', 'Preview');

  return (
    <>
      <Stack
        ref={cardRef}
        className={`bg-white rounded shadow p-5 ${className}`}
      >
        <div
          className={
            'd-flex flex-row justify-content-between align-items-center mb-2'
          }
        >
          <div className={'d-flex flex-row gap-3 align-items-center'}>
            <LomavisH3 title={t(title)} className="m-0" />
            {showMoreInfo && (
              <LomavisMoreInfoIcon text={t(`${title}.moreInfo`)} size={4} />
            )}
            {subtitle && <div className={'d-flex h-100'}>{subtitle}</div>}
          </div>
          <div className={'d-flex flex-row align-items-center mt-n4 gap-2'}>
            {enablePreview && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => setShowPreview(true)}
                aria-label={previewLabel}
              >
                <i
                  className="fa-duotone fa-solid fa-up-right-and-down-left-from-center"
                  aria-hidden="true"
                />
              </button>
            )}

            {showLinkButton && (
              <div className={''}>
                <a
                  href={linkButtonUrl || '#'}
                  className={'btn btn-sm btn-secondary'}
                >
                  {linkButtonText || t('viewMoreButton')}
                </a>
              </div>
            )}

            {showSheetDownloadButton && (
              <LomavisDownloadExcelElement
                excelData={excelData}
                fileName={`${title}`}
                elementRef={elementRef}
                dataExtractor={dataExtractor}
                staticData={staticData}
              />
            )}

            {showDownloadButton && (
              <div className={''}>
                <LomavisDownloadDomElementButton
                  elementRef={cardRef}
                  fileName={title}
                  onDownload={
                    customDownload
                      ? async () => await customDownload(cardRef)
                      : null
                  }
                />
              </div>
            )}
          </div>
        </div>
        {children}
      </Stack>

      {enablePreview && (
        <Modal
          show={showPreview}
          onHide={() => setShowPreview(false)}
          fullscreen
          centered
          scrollable
          dialogClassName="p-4"
          contentClassName="bg-transparent border-0"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="w-100 bg-light rounded shadow-sm overflow-hidden px-0"
            onClick={(e) => e.stopPropagation()}
          >
            <Modal.Header closeButton className="border-0 bg-light">
              <Modal.Title>{t(title)}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
              <div
                className="overflow-auto h-100 w-100 pb-4"
                style={{ maxHeight: 'calc(100vh - 100px)' }}
              >
                <div className="bg-white rounded shadow-sm p-4">
                  {subtitle && <div className="mb-3">{subtitle}</div>}
                  {React.Children.map(children, (child, index) =>
                    React.isValidElement(child)
                      ? React.cloneElement(child, { key: `preview-${index}` })
                      : child
                  )}
                </div>
              </div>
            </Modal.Body>
          </div>
        </Modal>
      )}
    </>
  );
};
